/***************************************************************************************************

 Copyright 2023-2024 Fannie Mae. All rights reserved.
 This software contains confidential information and trade secrets of Fannie Mae.
 Use, disclosure, or reproduction is prohibited without the prior written consent of Fannie Mae.

 ****************************************************************************************************/

import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core'
import { FmComponentsModule } from '@fm-ui-adk/components-3/components_module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AppRoutingModule } from './app.routes';
import { APICallService } from './api-call.service';
import { NotificationService } from './services/notification.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EndpointsService } from './services/endpoints.service';
import { HeaderComponent } from './component/header/header.component';
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatTableModule } from "@angular/material/table";
import { LoginGuard } from './login.guard';
import { ApiService } from './services/api.service';
import { SellerServiceNumberService } from './services/sellerservicernumber.service';
import { RouterModule } from '@angular/router';
import { PortalModule } from '@angular/cdk/portal';
import { GlobalErrorHandlerService } from './global-error-handler.service';
import { ErrorComponent } from './component/error/error.component';
import { DD_DashboardComponent } from './due-diligence/dashboard/dd-dashboard.component';
import { DashboardService } from "./services/dashboard.service";
import { FooterComponent } from "./component/footer/footer.component";
import { Utils } from './shared/utils';
import { IssueSummaryPreviewComponent } from './due-diligence/component/issue-summary-preview/issue-summary-preview.component';
import { HomeComponent } from './home/home.component';
import { DataFilesComponent } from './due-diligence/data-files/data-files.component';
import { MockInterceptor } from './mock.interceptor';
import { AcquisitionsUpdateComponent } from './due-diligence/component/acquisitions-update/acquisitions-update.component';
import { AcquisitionsComponent } from './due-diligence/component/acquisitions/acquisitions.component';
import { LenderRankingComponent } from './due-diligence/component/lender-ranking/lender-ranking.component';
import { UserNotificationComponent } from './component/user-notification/user-notification.component';
import { SystemDownComponent } from './component/system-down/system-down.component';
import { LaunchDarklyUtil } from './shared/launch-darkly-util';
import { MainBodyViewComponent } from './component/main-body-view/main-body-view.component';
import { ActionRequiredComponent } from './due-diligence/component/action-required/action-required.component';
import { HeaderTitleComponent } from './component/header-title/header-title.component';
import { AcquisitionsFilterComponent } from './due-diligence/component/acquisitions-filter/acquisitions-filter.component';
import { AcquisitionPropertyDetailsComponent } from './due-diligence/component/acquisition-property-details/acquisition-property-details.component';
import { AcqnFilterService } from './services/acqn-filter-service';
import { AqsnPropertyDetailsService } from './services/aqsn-property-details.service';
import { FileUploadComponent } from './due-diligence/component/file-upload/file-upload.component';
import { InsuranceComponent } from 'src/app/insurance/insurance.component';
import { AcquisitionsInsuranceComponent } from './insurance/component/acquisitions-insurance/acquisitions-insurance.component';
import { ActionRequiredInsuranceComponent } from './insurance/component/action-required-insurance/action-required-insurance.component';
import { AcquisitionsInsuranceFilterComponent } from './insurance/component/acquisitions-insurance-filter/acquisitions-insurance-filter.component'; 
import { AcquisitionInsurancePropertyDetailsComponent } from './insurance/component/acquisition-insurance-property-details/acquisition-insurance-property-details.component';
import { FileUploadInsuranceComponent } from './insurance/component/file-upload-insurance/file-upload-insurance.component';
import { IssueSummaryPreviewInsuranceComponent } from './insurance/component/issue-summary-preview-insurance/issue-summary-preview-insurance.component';
import { InsuranceService } from './services/insurance.service';
import { LenderRankingInsuranceComponent } from './insurance/component/lender-ranking-insurance/lender-ranking-insurance.component';
import { AcquisitionsInsuranceUpdateComponent } from './insurance/component/acquisitions-update-insurance/acquisitions-update-insurance.component';


@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    ErrorComponent,
    DD_DashboardComponent,
    FooterComponent,
    IssueSummaryPreviewComponent,
    HomeComponent,
    DataFilesComponent,
    AcquisitionsUpdateComponent,
    AcquisitionsComponent,
    AcquisitionsFilterComponent,
    AcquisitionPropertyDetailsComponent,
    LenderRankingComponent,
    UserNotificationComponent,
    SystemDownComponent,
    MainBodyViewComponent,
    ActionRequiredComponent,
    HeaderTitleComponent,
    FileUploadComponent,
    InsuranceComponent,
    AcquisitionsInsuranceComponent,
    ActionRequiredInsuranceComponent,
    AcquisitionInsurancePropertyDetailsComponent,
    AcquisitionsInsuranceFilterComponent,
    FileUploadInsuranceComponent,
    IssueSummaryPreviewInsuranceComponent,
    LenderRankingInsuranceComponent,
    AcquisitionsInsuranceUpdateComponent
  ],
  imports: [
    FmComponentsModule.forRoot(),
    BrowserAnimationsModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MatButtonModule,
    MatMenuModule,
    MatSidenavModule,
    MatTableModule,
    MatIconModule,
    AppRoutingModule,
    PortalModule
  ],
  exports: [
    FmComponentsModule,
    RouterModule],
  providers: [
    DashboardService,
    APICallService,
    NotificationService,
    EndpointsService,
    LoginGuard,
    SellerServiceNumberService,
    GlobalErrorHandlerService,
    ApiService,
    AcqnFilterService,
    AqsnPropertyDetailsService,
    InsuranceService,
    Utils,
    LaunchDarklyUtil,
    { provide: HTTP_INTERCEPTORS, useClass: ApiService, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: MockInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
